import _ from 'lodash';
import { all, call, CallEffect, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { LiveEventParamKeyGenerator } from '../../../../common/eventUtils';
import { safeParseJson } from '../../../../common/utils';
import { ILiveEventSummary, LiveEventMode } from '../../../../types/eventTypes';
import { TAction } from '../../actions/types/action';
import { featuresSelector, getFeatureParam } from '../../selectors/global';
import { retryableCommonApiServiceAsync, TGetLiveEventParams } from '../../services/commonApiService';
import { actions as threadActions } from '../../thread/store/modules';
import {
  getDefaultMastheadLayout,
  MastheadComponentConfigOrType,
  normalizeMastheadLayoutConfig
} from '../liveEventMasthead/hooks';
import { LiveMastheadComponents, TLiveMastheadComponentConfig } from '../liveEventMasthead/types';
import { getRunRankingsData, isBracketFormatEligibleForRankings } from '../utils/getRunRankingsData';
import { eventHubActions, eventHubActionTypes } from './modules';
import {
  eventHubActiveEventsSelector,
  eventHubExpansionCollectionsSelector,
  eventHubHubCollectionsSelector,
  eventHubLiveEventSelector,
  eventHubNewsSelector,
  eventHubUpcomingEventsSelector
} from './selectors';
import { TGetLiveEventActionParams } from './types';

function* getExpansionCollectionsSaga(action: any): any {
  try {
    yield put(
      eventHubActions.setExpansionCollections({
        isLoaded: false,
        isLoading: true,
        list: []
      })
    );

    const data = yield call(retryableCommonApiServiceAsync.getExpansionCollections);

    yield put(
      eventHubActions.setExpansionCollections({
        isLoaded: true,
        isLoading: false,
        list: data
      })
    );
  } catch (error) {
    console.error('Error in getExpansionCollectionsSaga', error);
    const expansionCollectionsState = yield select(eventHubExpansionCollectionsSelector);

    yield put(
      eventHubActions.setExpansionCollections({
        isLoaded: true,
        isLoading: false,
        list: expansionCollectionsState.list || []
      })
    );
  }
}

function* getLiveEventSaga(
  action: TAction<{
    keepPreviousData: boolean;
    params: TGetLiveEventActionParams;
  }>
): any {
  const currentLiveEvent = yield select(eventHubLiveEventSelector);

  const isUIMatch = _.isEqual(_.pickBy(currentLiveEvent.ui, _.identity), _.pickBy(action.payload.params, _.identity));
  const keepPreviousData = action.payload.keepPreviousData && isUIMatch;

  try {
    yield put(
      eventHubActions.updateLiveEventState({
        isLoaded: keepPreviousData ? currentLiveEvent.isLoaded : false,
        isLoading: true,
        data: keepPreviousData ? currentLiveEvent.data : null,
        ui: action.payload.params
      })
    );

    const getLiveEventParams: TGetLiveEventParams = _.omit(action.payload.params, 'rankingsLimit');
    const data = (yield call(retryableCommonApiServiceAsync.getLiveEvent, getLiveEventParams)) as ILiveEventSummary;

    yield put(
      eventHubActions.updateLiveEventState({
        isLoaded: true,
        isLoading: false,
        isCoverageLoading: !!data?.coverageInfo,
        data,
        coverageInfo: data?.coverageInfo
      })
    );

    if (data?.bracket || data?.coverageInfo) {
      let rankingsPromise: CallEffect<any> | null = null;

      if (data?.bracket && isBracketFormatEligibleForRankings(data.bracket)) {
        const features = yield select(featuresSelector);
        const mastheadLayoutKey = LiveEventParamKeyGenerator.getMastheadLayoutKey(
          action.payload.params.mode || LiveEventMode.HOME
        );
        const newMastheadLayoutString = getFeatureParam(features, mastheadLayoutKey);
        const defaultLayout = getDefaultMastheadLayout(data);

        const paramConfig = newMastheadLayoutString
          ? safeParseJson<MastheadComponentConfigOrType[][]>(newMastheadLayoutString, undefined) || defaultLayout
          : defaultLayout;

        const eventConfig = paramConfig
          .flat()
          .find((config) => (typeof config === 'object' ? config.component === 'event' : config === 'event'));

        const normalizedConfig = eventConfig
          ? (normalizeMastheadLayoutConfig(eventConfig) as TLiveMastheadComponentConfig & {
              component: LiveMastheadComponents.Event;
            })
          : null;

        rankingsPromise = call(
          getRunRankingsData,
          {
            bracket: data.bracket,
            eventSlug: data.event.slug,
            rankingsLimit: action.payload.params.rankingsLimit,
            eventConfig: normalizedConfig
          },
          { retry: true }
        );
      }

      const coveragePromise = data?.coverageInfo
        ? call(retryableCommonApiServiceAsync.peekThreadPosts, {
            slug: data.coverageInfo.slug,
            limit: data.coverageInfo.limit
          })
        : null;

      try {
        const [rankingsData, coveragePosts] = yield all([rankingsPromise, coveragePromise]);

        if (rankingsData) {
          yield put(
            eventHubActions.updateLiveEventState({
              teamRankings: rankingsData.rankingsByTeam,
              runByDungeonRankings: rankingsData.rankingsByDungeon
            })
          );
        }

        if (coveragePosts) {
          yield put(threadActions.setPeekThreadPostsSucceeded(coveragePosts));
        }

        if (coveragePromise) {
          yield put(eventHubActions.updateLiveEventState({ isCoverageLoading: false }));
        }
      } catch (error) {
        console.error('Error in getLiveEventSaga - parallel calls', error);
      }
    }
  } catch (error) {
    console.error('Error in getLiveEventSaga', error);
    yield put(
      eventHubActions.updateLiveEventState({
        isLoaded: true,
        isLoading: false,
        data: currentLiveEvent.data,
        coverageInfo: currentLiveEvent.coverageInfo,
        isCoverageLoading: false
      })
    );
  }
}

function* getHubCollectionsSaga(action: any): any {
  const currentHubCollections = yield select(eventHubHubCollectionsSelector);
  try {
    yield put(
      eventHubActions.setHubCollections({
        isLoaded: false,
        isLoading: true,
        list: currentHubCollections.list,
        ui: action.payload
      })
    );

    const data = yield call(retryableCommonApiServiceAsync.getHubCollections, action.payload);

    yield put(
      eventHubActions.setHubCollections({
        isLoaded: true,
        isLoading: false,
        list: data,
        ui: action.payload
      })
    );
  } catch (error) {
    console.error('Error in getHubCollectionsSaga', error);

    yield put(
      eventHubActions.setHubCollections({
        isLoaded: true,
        isLoading: false,
        list: [],
        ui: action.payload
      })
    );
  }
}

function* getUpcomingEventsSaga(action: any): any {
  const upcomingEventsState = yield select(eventHubUpcomingEventsSelector);
  try {
    yield put(
      eventHubActions.setUpcomingEvents({
        isLoaded: false,
        isLoading: true,
        ui: action.payload,
        data: upcomingEventsState.data
      })
    );

    const data = yield call(retryableCommonApiServiceAsync.getUpcomingEvents, action.payload);

    yield put(
      eventHubActions.setUpcomingEvents({
        isLoaded: true,
        isLoading: false,
        ui: action.payload,
        data
      })
    );
  } catch (error) {
    console.error('Error in getUpcomingEventsSaga', error);

    yield put(
      eventHubActions.setUpcomingEvents({
        isLoaded: true,
        isLoading: false,
        ui: action.payload,
        data: {}
      })
    );
  }
}

function* getActiveEventsSaga(action: any): any {
  const activeEventsState = yield select(eventHubActiveEventsSelector);

  try {
    yield put(
      eventHubActions.setActiveEvents({
        isLoaded: false,
        isLoading: true,
        ui: action.payload,
        data: activeEventsState.data
      })
    );

    const data = yield call(retryableCommonApiServiceAsync.getActiveEvents, action.payload);

    yield put(
      eventHubActions.setActiveEvents({
        isLoaded: true,
        isLoading: false,
        ui: action.payload,
        data
      })
    );
  } catch (error) {
    console.error('Error in getActiveEventsSaga', error);

    yield put(
      eventHubActions.setActiveEvents({
        isLoaded: true,
        isLoading: false,
        ui: action.payload,
        data: {}
      })
    );
  }
}

function* getLatestEventSaga(action: any): any {
  try {
    yield put(
      eventHubActions.setLatestEvent({
        isLoaded: false,
        isLoading: true,
        data: [],
        ui: action.payload
      })
    );

    const data = yield call(retryableCommonApiServiceAsync.getLatestEvent, action.payload);

    yield put(
      eventHubActions.setLatestEvent({
        isLoaded: true,
        isLoading: false,
        ui: action.payload,
        data
      })
    );
  } catch (error) {
    console.error('Error in getLatestEventSaga', error);

    yield put(
      eventHubActions.setLatestEvent({
        isLoaded: true,
        isLoading: false,
        ui: action.payload,
        data: []
      })
    );
  }
}

function* getEventsHistorySaga(action: any): any {
  try {
    yield put(
      eventHubActions.setEventsHistoryByExpansion({
        isLoaded: false,
        isLoading: true,
        data: [],
        ui: action.payload
      })
    );

    const data = yield call(retryableCommonApiServiceAsync.getEventsHistory, action.payload);

    yield put(
      eventHubActions.setEventsHistoryByExpansion({
        isLoaded: true,
        isLoading: false,
        data: data,
        ui: action.payload
      })
    );
  } catch (error) {
    console.error('Error in getWinnersHistorySaga', error);

    yield put(
      eventHubActions.setEventsHistoryByExpansion({
        isLoaded: true,
        isLoading: false,
        data: [],
        ui: action.payload
      })
    );
  }
}

function* getNewsSaga(action: TAction<any>): any {
  try {
    yield put(
      eventHubActions.setNews({
        isLoaded: false,
        isLoading: true,
        list: [],
        ui: action.payload
      })
    );

    const data = yield call(retryableCommonApiServiceAsync.getNewsListContent, action.payload);

    yield put(
      eventHubActions.setNews({
        isLoaded: true,
        isLoading: false,
        list: data.articles,
        ui: action.payload
      })
    );
  } catch (error) {
    console.error('Error in getNewsSaga', error);
    const newsState = yield select(eventHubNewsSelector);

    yield put(
      eventHubActions.setNews({
        isLoaded: true,
        isLoading: false,
        list: newsState.list || [],
        ui: action.payload
      })
    );
  }
}

function* watchRequestGetExpansionCollections() {
  yield takeEvery(eventHubActionTypes.GET_EXPANSION_COLLECTIONS, getExpansionCollectionsSaga);
}

function* watchRequestGetHubCollections() {
  yield takeEvery(eventHubActionTypes.GET_HUB_COLLECTIONS, getHubCollectionsSaga);
}

function* watchRequestGetLiveEvent() {
  yield takeLatest(eventHubActionTypes.GET_LIVE_EVENT, getLiveEventSaga);
}

function* watchRequestGetUpcomingEvents() {
  yield takeEvery(eventHubActionTypes.GET_UPCOMING_EVENTS, getUpcomingEventsSaga);
}

function* watchRequestGetLatestEvent() {
  yield takeEvery(eventHubActionTypes.GET_LATEST_EVENT, getLatestEventSaga);
}

function* watchRequestGetEventsHistory() {
  yield takeEvery(eventHubActionTypes.GET_EVENTS_HISTORY, getEventsHistorySaga);
}

function* watchRequestGetNews() {
  yield takeEvery(eventHubActionTypes.GET_NEWS, getNewsSaga);
}

function* watchRequestGetActiveEvents() {
  yield takeEvery(eventHubActionTypes.GET_ACTIVE_EVENTS, getActiveEventsSaga);
}

export default [
  watchRequestGetExpansionCollections(),
  watchRequestGetHubCollections(),
  watchRequestGetLiveEvent(),
  watchRequestGetUpcomingEvents(),
  watchRequestGetLatestEvent(),
  watchRequestGetEventsHistory(),
  watchRequestGetNews(),
  watchRequestGetActiveEvents()
];
